import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { Button, Icon, Text } from '@BICE_Vida/components';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import classNames from 'classnames';
import { Container, Row, Col } from '../../designSystem/Container';
import { Wizard as WizardDS, WizardFooter, WizardIconBack } from '../../designSystem/Wizard';

import Header from '../Header';
import ReviewInfoCapsule from '../ReviewInfoCapsule';

import styles from './Wizard.module.scss';

import { dataLayerPush } from '../../utils/commonFunctions';
import { breakPointMd } from '../../utils/constants';
import { summaryStepTexts } from '../../utils/generalTexts';
import componentsId from '../../utils/componentsId';
import routes from '../../utils/routes';

import * as wizardSelectors from '../../redux/wizard/selectors';
import * as hiringSelectors from '../../redux/hiring/selectors';
import * as plansSelectors from '@/redux/plans/selectors';

import { StepComponent } from '../../types/estimate';
import PromoHeaderFlow from '../PromoHeader/PromoHeaderFlow';
import { useCyberDates } from '@/src/customHooks';
import PreexistingConditionsHeader from '../PreexistingConditionsHeader';

import { AvailablePlans as AvailablePlansType } from '../../types/availablePlans';
import FooterWorker from '@/designSystem/FooterWorker';

type EstimateStepT = {
  stepComponent: StepComponent;
  stepsQuantity: number;
};

function WizardRender({ stepComponent }: EstimateStepT) {
  const [smallSize, setScreenSize] = useState(false);
  const shouldShowReview = stepComponent.priceAlterator && !stepComponent.isLastStep;
  const branding = useSelector(wizardSelectors.getBranding);
  const availablePlans: AvailablePlansType = useSelector(plansSelectors.getAvailablePlans);

  const router = useRouter();
  const { name: executiveName } = useSelector(wizardSelectors.getExecutive);

  const promoCyberData = useCyberDates(new Date());

  const { cyberDay } = promoCyberData;

  const leadCreated = useSelector(hiringSelectors.getLeadCreated);

  const btnHiringClick = () => {
    router.push({
      pathname: routes.hiringDashboard,
      query: { lead: leadCreated },
    });
    dataLayerPush('trackEvent', 'mipymesegura_cotizacion', 'cotizacion_resumen cotizacion', {
      eventLabel: 'boton - contratar online',
    });
  };

  const onResize = () => {
    setScreenSize(window.innerWidth <= breakPointMd);
  };

  useEffect(() => {
    window.addEventListener('resize', onResize);
    onResize();

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const Executive = (executive: string) => {
    return (
      <div className={styles.executive_section}>
        <Icon withBg name="support_agent" size="xsmall" />
        <Text fontTypo="body-small">{executive}</Text>
      </div>
    );
  };

  const availablePlansFiltered = useMemo(
    () => availablePlans.filter((card) => card.base !== null && card.hidePlan !== true),
    [availablePlans]
  );

  const showCyberBanner = cyberDay !== null && !branding?.code;

  const showPreexistingConditionsBanner = false;

  const cyberHeaderClasss = classNames(styles.header, {
    [styles.cyber_header]: showCyberBanner,
    [styles.header_preexisting]: showPreexistingConditionsBanner,
  });
  const headerWizardClass = classNames({
    [styles.wizard_header]: showCyberBanner,
    [styles.wizard_header_preexisting]: showPreexistingConditionsBanner,
  });

  const wizardContainerClass = classNames(styles.wizard_container, {
    [styles.wizard_container_cyber]: showCyberBanner,
    [styles.wizard_container_preexisting]: showPreexistingConditionsBanner,
  });

  return (
    <Fragment>
      {!stepComponent.isLastStep && (
        <>
          {showPreexistingConditionsBanner && (
            <PreexistingConditionsHeader
              availablePlans={availablePlansFiltered.length}
              className={styles.preexisting_conditions_header}
            />
          )}
          {!!showCyberBanner && <PromoHeaderFlow {...promoCyberData} />}
          <Header
            wizard={!smallSize}
            customClass={headerWizardClass}
            brandingLogo={branding?.logo}
            brandingCode={branding?.code}
            rightComponent={!!executiveName ? Executive(executiveName) : <></>}
            sticky={false}
          />
          <WizardDS
            showBackBtnAlways
            currentStep={stepComponent.id}
            customClass={styles.wizard}
            iconBack={
              !smallSize ? (
                <WizardIconBack
                  onClick={stepComponent.onPreviousTransition}
                  customClass={cyberHeaderClasss}
                />
              ) : undefined
            }
            wizardHeader={shouldShowReview && <ReviewInfoCapsule customClass={cyberHeaderClasss} />}
            wizardFooter={
              <WizardFooter
                customClass={styles.wizard_footer}
                nextBtn={{
                  id: `${stepComponent.elementsIds.btnNext}_mobile`,
                  onClick: stepComponent.onNextTransition,
                  text: stepComponent.texts.btnNext(smallSize),
                  disabled: stepComponent.isDisabled(),
                  loading: stepComponent.isLoading(),
                }}
                backBtn={{
                  id: `${stepComponent.elementsIds.btnBack}_mobile`,
                  onClick: stepComponent.onPreviousTransition,
                  text: stepComponent.texts.btnBack,
                  disabled: false,
                  loading: false,
                }}
              />
            }
          >
            <Fragment>
              <Container>
                <Row>
                  <Col className={wizardContainerClass}>
                    <div id={stepComponent.componentId}>{stepComponent.component()}</div>
                    <div className={styles.desktop_btn_group}>
                      {stepComponent.id > 0 && (
                        <Button
                          id={stepComponent.elementsIds.btnBack}
                          text={stepComponent.texts.btnBack}
                          size="large"
                          variant="text"
                          onClick={stepComponent.onPreviousTransition}
                          disabled={false}
                        />
                      )}
                      <Button
                        id={stepComponent.elementsIds.btnNext}
                        text={stepComponent.texts.btnNext(smallSize)}
                        size="large"
                        onClick={stepComponent.onNextTransition}
                        disabled={stepComponent.isDisabled()}
                        loading={stepComponent.isLoading()}
                      />
                    </div>
                  </Col>
                </Row>
              </Container>
            </Fragment>
            {!smallSize && <FooterWorker />}
          </WizardDS>
        </>
      )}

      {stepComponent.isLastStep && (
        <>
          <Header brandingLogo={branding?.logo} brandingCode={branding?.code} />
          <Container>
            <Row>
              <Col>
                <div id={stepComponent.componentId} className={styles.wizard_container}>
                  {stepComponent.component()}
                </div>
              </Col>
            </Row>
          </Container>
          {!smallSize && <FooterWorker />}
          {smallSize && (
            <div className={styles.summary_mobile_footer}>
              <Button
                id={componentsId.summaryStep.btnBackMobile}
                text={summaryStepTexts.btnBackToLanding}
                size="medium"
                variant="text"
                onClick={stepComponent.onPreviousTransition}
              />
              <Button
                id={componentsId.summaryStep.btnHiringMobile}
                text={summaryStepTexts.btnHiring(true)}
                size="medium"
                onClick={btnHiringClick}
              />
            </div>
          )}
        </>
      )}
    </Fragment>
  );
}

export default WizardRender;
